import { ReactNode, useState } from "react"
import AppLayout from "@/components/Layout/App"
import Alert from "@/components/Alert"
import SearchForm from "@/components/SearchForm"
import axios from "axios"
import { router } from "@inertiajs/react"
import clsx from "clsx"
import { Alert as AlertType, Currency } from "@/types/types"
import { PlusIcon } from "@heroicons/react/20/solid"

const Alerts = ({ alerts }: { alerts: AlertType[] }) => {
  const [currencies, setCurrencies] = useState<Currency[]>([])
  const [isFetching, setIsFetching] = useState(false)
  const [isAdding, setIsAdding] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const fetchCurrencies = async () => {
    if (currencies.length === 0) {
      setIsFetching(true)
      const { data } = await axios.get<Currency[]>("/currencies.json")
      setCurrencies(data)
      setIsFetching(false)
    }
    setIsAdding(true)
  }

  const addAlert = async ({
    currency,
    threshold,
  }: {
    currency: Currency
    threshold: number
  }) => {
    setIsSubmitting(true)
    router.post(
      "/alerts",
      {
        alert: {
          currency_id: currency.id,
          change_threshold: threshold,
        },
      },
      {
        onFinish: () => {
          setIsSubmitting(false)
          setIsAdding(false)
        },
        preserveState: true,
        preserveScroll: true,
      }
    )
  }

  const hasAlerts = alerts.length > 0

  return (
    <div className="mx-auto w-full max-w-2xl px-2 py-10">
      <div className="space-y-1">
        {alerts.map((alert) => (
          <Alert key={alert.id} alert={alert} />
        ))}
      </div>
      <div className="relative my-10">
        <button
          onClick={() => (isAdding ? setIsAdding(false) : fetchCurrencies())}
          className={clsx(
            "mx-auto flex h-12 items-center justify-center rounded-full bg-gray-800 text-sm text-gray-500 hover:text-white",
            hasAlerts ? "w-12" : "pr-5 pl-4"
          )}
        >
          <span className="flex w-6 shrink-0 items-center justify-center">
            {isFetching ? (
              <div className="size-4 animate-spin rounded-full border-2 border-gray-500 border-r-transparent" />
            ) : (
              <PlusIcon
                className={clsx(
                  "size-6 fill-current transition duration-100 ease-in-out",
                  isAdding && "rotate-45"
                )}
              />
            )}
          </span>
          <span
            className={clsx(
              hasAlerts ? "sr-only" : "mt-0.5 ml-2 text-sm leading-5"
            )}
          >
            Add an alert
          </span>
        </button>
      </div>
      {isAdding && (
        <div
          className={clsx({ "pointer-events-none opacity-75": isSubmitting })}
        >
          <SearchForm
            onSelect={addAlert}
            currencies={currencies.filter(
              (c) => !alerts.map((a) => a.currency.id).includes(c.id)
            )}
          />
        </div>
      )}
    </div>
  )
}

Alerts.layout = (page: ReactNode) => <AppLayout children={page} />

export default Alerts
